import type { JSX } from "react";
import { FC } from "react";
import styled from "styled-components";
import { color as themeColor } from "@styles";
import { HeadingSize, H3 } from "../../atoms/Heading";
import { P1 } from "../../atoms/Paragraph";
import { Tab, HandleClick, TabSwitcherVariation } from ".";
import { Image } from "../../atoms/Image";
import { StandardSection } from "../../templates/StandardSection";
import { variationColor } from "./utils";

export const TabBar: FC<
  React.PropsWithChildren<{
    controls?: string;
    tabs: Tab[];
    tabTitles: string[];
    handleClick: HandleClick;
    selectedTab: number;
    hugTop?: boolean;
    callToAction?: string;
    variation?: TabSwitcherVariation;
  }>
> = ({ controls, tabs, tabTitles, handleClick, selectedTab, hugTop, variation }) => {
  return (
    <TabWrap hugTop={hugTop}>
      <TabBarBackground variation={variation} />
      <Blob
        variation={variation}
        position={selectedTab}
        isFirst={selectedTab === 0}
        isLast={selectedTab === tabTitles.length - 1}
        tabCount={tabTitles.length}
      />
      <UL role="tablist">
        {tabs.map(({ title }, index) => {
          return (
            <LI
              variation={variation}
              key={title}
              tabIndex={0}
              onKeyPress={(e) => e.key === "Enter" && handleClick(index)}
              onClick={() => handleClick(index)}
              isSelected={index === selectedTab}
              role="tab"
              aria-selected={index === selectedTab}
              aria-controls={controls}
            >
              {title}
            </LI>
          );
        })}
      </UL>
    </TabWrap>
  );
};

export const TabSwitcherDesktop: FC<
  React.PropsWithChildren<{
    id?: string;
    tabs: Tab[];
    tabTitles: string[];
    handleClick: HandleClick;
    selectedTab: number;
    imageOnly?: boolean;
    clouds?: JSX.Element;
    pb?: string;
    hugTop?: boolean;
    variation?: TabSwitcherVariation;
    footnote?: string;
  }>
> = ({
  id,
  tabs,
  tabTitles,
  handleClick,
  selectedTab,
  imageOnly,
  clouds,
  pb,
  hugTop,
  variation,
}) => {
  const { heading, content, callToAction, url, image, alt, footnote } = tabs[selectedTab];

  return (
    <StandardSection pt="1rem" pb={pb}>
      <div style={{ width: "100%" }}>
        <TabBar
          controls={`${id}-tabswitcherdesktop-tab`}
          tabs={tabs}
          tabTitles={tabTitles}
          selectedTab={selectedTab}
          handleClick={handleClick}
          hugTop={hugTop}
          variation={variation}
        />
        <FlexRow
          hugTop={hugTop}
          id={`${id}-tabswitcherdesktop-tab`}
          aria-live="assertive"
          aria-atomic
          aria-labelledby={`${id}-tabswitcherdesktop-title`}
          role="tabpanel"
        >
          {clouds || null}
          {imageOnly ? null : (
            <div style={{ width: "100%", textAlign: "left" }}>
              <TextWrapper>
                <SectionTitle id={`${id}-tabswitcherdesktop-title`} size={HeadingSize.H3} pt="0">
                  {heading}
                </SectionTitle>
                <ContentSection>{content}</ContentSection>
                {footnote && <Footnote>{footnote}</Footnote>}
                {url && (
                  <FindOutMoreLink href={url}>{callToAction || "Find out more"}</FindOutMoreLink>
                )}
              </TextWrapper>
            </div>
          )}
          {image ? (
            <ImageContainer>
              {typeof image === "string" ? <Image key={image} path={image} alt={alt} /> : image}
            </ImageContainer>
          ) : (
            <Placeholder imageOnly={imageOnly} />
          )}
        </FlexRow>
        {imageOnly ? <P1 style={{ margin: "2rem 0" }}>{content}</P1> : null}
      </div>
    </StandardSection>
  );
};

const TabBarBackground = styled.div<{ variation?: TabSwitcherVariation }>`
  background: ${({ variation }) =>
    variation ? variationColor(variation).tabBackground : themeColor.S320};
  width: 100%;
  left: 0;
  top: 4rem;
  min-height: 4rem;
  border-radius: 0.5rem;
  position: relative;
  pointer-events: none;
  box-shadow: ${({ variation }) =>
    variation ? variationColor(variation).boxShadow : "0.5rem 0.5rem 0 0 rgba(149, 106, 255, 0.1)"};
`;

const Blob = styled.div<{
  position: number;
  isFirst: boolean;
  isLast: boolean;
  tabCount: number;
  variation?: TabSwitcherVariation;
}>`
  background: ${({ variation }) => (variation ? variationColor(variation).blob : themeColor.S3100)};
  width: ${(p) => (p.tabCount ? 100 / p.tabCount : 100)}%;
  left: ${(p) => (p.position && p.tabCount ? (100 / p.tabCount) * p.position : 0)}%;
  border-radius: ${(p) => (p.isFirst ? "0.5rem 0 0 0.5rem" : p.isLast ? "0 0.5rem 0.5rem 0" : "0")};
  min-height: 4rem;
  position: absolute;
  pointer-events: none;
  transition: 400ms ease-in-out;
`;

const UL = styled.ul`
  display: flex;
  width: 100%;
  justify-content: space-between;
  position: absolute;
  white-space: nowrap;
`;

const LI = styled.li<{ isSelected: boolean; variation?: TabSwitcherVariation }>`
  flex: 1;
  text-align: center;
  border: ${({ variation }) =>
    variation ? `1px solid ${variationColor(variation).border}` : `1px solid ${themeColor.S3100}`};
  border-right: none;
  padding: 0.75rem 0.5rem;
  color: ${(p) =>
    p.isSelected ? "#FFFFFF" : p.variation ? variationColor(p.variation).text : themeColor.S3100};
  font-weight: bold;
  font-size: 1.25rem;
  min-height: 4rem;
  cursor: pointer;
  transition: 600ms ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  &:first-of-type {
    border-radius: 0.5rem 0 0 0.5rem;
  }

  &:last-of-type {
    border-radius: 0 0.5rem 0.5rem 0;
    border-right: 1px solid ${themeColor.S3100};
  }
`;

const FlexRow = styled.div<{ hugTop: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: ${(p) => (p.hugTop ? "10rem" : "7rem")};
  position: relative;
`;

const TextWrapper = styled.div`
  max-width: 34rem;
  margin-right: 9vw;
  transition: 200ms ease-in-out;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 35.5rem;

  @media only screen and (max-width: 65rem) {
    max-width: 28rem;
    margin-top: 0.5rem;
    margin-right: 2rem;
    margin-bottom: 0.75rem;
  }
`;

const ImageContainer = styled.div`
  padding-top: 2rem;
  position: relative;
  width: 100%;
  height: 37.5rem;
  svg,
  img {
    object-fit: contain;
    border-radius: 0.5rem;
    max-width: 34rem;
  }

  video {
    border-radius: 0.5rem;
    width: 34rem;
    height: 34rem;
    object-fit: cover;
  }
`;

const Placeholder = styled.div<{ imageOnly?: boolean }>`
  width: 100%;
  max-width: ${(p) => (p.imageOnly ? "100%" : "34rem")};
  height: 37.5rem;
  background: ${themeColor.S320};
  border-radius: 0.5rem;
`;

const Anchor = styled.a`
  text-decoration: none;
  color: #fff;

  &:hover {
    color: #fff;
  }
`;

const ContentSection = styled.p`
  max-width: 32rem;
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 2rem;
  letter-spacing: 0.075rem;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
`;

const SectionTitle = styled(H3)`
  max-width: 29rem;
`;

const TabWrap = styled.div<{ hugTop?: boolean }>`
  position: relative;
  margin-top: ${(p) => (p.hugTop ? "-6.875rem" : "0")};
`;

const FindOutMoreLink = styled.a`
  color: ${themeColor.P100};
  font-size: 1.25rem;
  letter-spacing: 0.075rem;
  line-height: 2rem;
  font-weight: 400;
  text-decoration-line: underline;
`;

const Footnote = styled.p`
  color: ${themeColor.B2100};
  font-size: 1rem;
  letter-spacing: 0.075rem;
  line-height: 2rem;
  font-weight: 400;
  padding-bottom: 1.5rem;
`;
